import type { KeyboardEvent, MouseEvent } from "react";
import { Field, useForm } from "react-final-form";
import MeritoCheckbox from "../../../../../components/common/MeritoCheckbox/MeritoCheckbox";
import MeritoPasswordField from "../../../../../components/common/MeritoPasswordField/MeritoPasswordField";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import { CREDENTIAL_FIELD } from "../../../api/connectionHub.types";
import type { CredentialChannelModel, CredentialFormChannel } from "../../modal/CredentialsStep/CredentialsStep.types";
import { requiresInbox } from "../../modal/CredentialsStep/credentials.utils";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import EmailSuggestInput from "../EmailSuggestInput/EmailSuggestInput";
import InboxOwnerToggle from "../InboxOwnerToggle/InboxOwnerToggle";
import OtpModeSelect from "../OtpModeSelect/OtpModeSelect";
import styles from "./ChannelCredentialCard.module.scss";

export interface ChannelCredentialCardProps {
  model: CredentialChannelModel;
  value: CredentialFormChannel;
  suggestions: string[];
  disabled?: boolean;
  /** Edit mode: the card belongs to an existing account, so its email is fixed and it cannot be dropped. */
  lockEmail?: boolean;
  removable?: boolean;
  onRemove: () => void;
}

export const ChannelCredentialCard = ({ model, value, suggestions, disabled = false, lockEmail = false, removable = true, onRemove }: ChannelCredentialCardProps): JSX.Element => {
  const form = useForm();
  const { channel, formIndex } = model;
  const prefix = `channels.${formIndex}`;
  const capability = channel.capability;
  const showEmail = capability.fields.includes(CREDENTIAL_FIELD.EMAIL);
  const showPassword = capability.fields.includes(CREDENTIAL_FIELD.PASSWORD);
  const stopAndRemove = (event: MouseEvent<HTMLDivElement>): void => { event.stopPropagation(); if (!disabled) onRemove(); };
  const removeWithKeyboard = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    if (!disabled) onRemove();
  };

  return (
    <article className={styles.card} aria-label={`${channel.ipName} credentials`}>
      <header className={styles.header}>
        <div className={styles.identity}>
          {removable ? (
            <div className={styles.removeControl} role="checkbox" aria-checked="true" aria-label={`Remove ${channel.ipName}`} tabIndex={disabled ? -1 : 0} onClick={stopAndRemove} onKeyDown={removeWithKeyboard}>
              <MeritoCheckbox checked="yes" state={disabled ? "disabled" : "default"} size="md2" />
            </div>
          ) : null}
          <ConnectionLogo logoUrl={channel.logo} className={styles.logoFrame} />
          <TruncateTooltip text={channel.ipName} className={styles.name} />
        </div>
      </header>

      <div className={`${styles.fields} ${showPassword ? styles.twoColumns : ""}`}>
        {showEmail ? <EmailSuggestInput name={`${prefix}.email`} suggestions={suggestions} disabled={disabled || lockEmail} /> : null}
        {showPassword ? (
          <Field<string> name={`${prefix}.password`}>
            {({ input, meta }) => (
              <label className={`${styles.passwordField} ${meta.touched && meta.error ? styles.invalid : ""}`}>
                <span>Password</span>
                <MeritoPasswordField
                  value={input.value ?? ""}
                  disabled={disabled}
                  placeholder={value.hasStoredPassword && !value.passwordEdited ? "••••••••" : "Enter password"}
                  error={Boolean(meta.touched && meta.error)}
                  onChange={(password) => form.batch(() => {
                    form.change(`${prefix}.password`, password);
                    form.change(`${prefix}.passwordEdited`, true);
                  })}
                />
                {meta.touched && meta.error ? <span className={styles.error}>{meta.error}</span> : null}
              </label>
            )}
          </Field>
        ) : null}
      </div>

      <OtpModeSelect name={`${prefix}.otpMode`} modes={capability.otpModes} ownerFieldName={`${prefix}.inboxOwner`} disabled={disabled} />
      {requiresInbox(capability, value.otpMode) ? (
        <InboxOwnerToggle ownerName={`${prefix}.inboxOwner`} email={value.email ?? ""} disabled={disabled} />
      ) : null}
    </article>
  );
};

export default ChannelCredentialCard;
